import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus, MagnifyingGlass, Receipt, FilePdf,
  Trash, PencilSimple, PaperPlaneTilt
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import YearMonthNav from '../components/YearMonthNav';

import { API } from '../config/api';

const statusColors = {
  bozza: 'bg-gray-100 text-gray-700 border-gray-200',
  inviata_sdi: 'bg-blue-100 text-blue-700 border-blue-200',
  accettata: 'bg-green-100 text-green-700 border-green-200',
  rifiutata: 'bg-red-100 text-red-700 border-red-200',
  pagata: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export default function InvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invoices`);
      setList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('invoice.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success(t('invoice.deleted'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('invoice.deleteError'));
    }
  };

  const handleDownloadPdf = async (item) => {
    try {
      const response = await axios.get(`${API}/invoices/${item.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${item.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(t('invoice.pdfError'));
    }
  };

  const handleSendSDI = async (item) => {
    if (!window.confirm(t('invoice.sendSDIConfirm'))) return;
    try {
      await axios.post(`${API}/sdi/send-invoice/${item.id}`);
      toast.success(t('invoice.sentSDI'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('invoice.sendSDIError'));
    }
  };

  const filtered = (list || []).filter(d => {
    const matchSearch = d.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.client_name?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    const dateStr = d.date || '';
    if (dateStr.length < 7) return false;
    const year = parseInt(dateStr.substring(0, 4));
    if (year !== selectedYear) return false;
    if (selectedMonth !== null) {
      const month = parseInt(dateStr.substring(5, 7)) - 1;
      if (month !== selectedMonth) return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="invoice-title">
            <Receipt size={28} weight="duotone" className="text-[#0033A0]" />
            {t('invoice.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">{t('invoice.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/fatture/new')}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white shrink-0"
          data-testid="new-invoice-button"
        >
          <Plus size={20} className="mr-2" />
          {t('invoice.newInvoice')}
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
        <Input
          placeholder={t('invoice.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-[#E2E8F0]"
          data-testid="invoice-search"
        />
      </div>

      <YearMonthNav
        items={list}
        dateField="date"
        totalField="total"
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />

      {loading ? (
        <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="text-center py-16">
            <Receipt size={48} className="mx-auto text-[#CBD5E0] mb-4" weight="duotone" />
            <h3 className="font-medium text-[#0A0F1A] mb-2">{t('invoice.noInvoices')}</h3>
            <p className="text-sm text-[#4A5568] mb-4">{t('invoice.createFirst')}</p>
            <Button
              onClick={() => navigate('/fatture/new')}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="create-first-invoice"
            >
              <Plus size={20} className="mr-2" />
              {t('invoice.newInvoice')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <Card key={item.id} className="border-[#E2E8F0] hover:border-[#CBD5E0] transition-colors" data-testid={`invoice-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-outfit font-bold text-[#0033A0]">{item.invoice_number}</span>
                      <Badge className={`text-[10px] ${statusColors[item.status] || 'bg-gray-100 text-gray-600'}`}>
                        {t(`invoice.statusLabels.${item.status}`) || item.status}
                      </Badge>
                      {item.sdi_status && (
                        <span className="text-[10px] text-[#4A5568] bg-[#F0FFF4] px-2 py-0.5 rounded border border-green-200">
                          SDI: {item.sdi_status}
                        </span>
                      )}
                      {item.linked_proforma_id && (
                        <span className="text-[10px] text-[#4A5568] bg-[#F4F6F8] px-2 py-0.5 rounded">
                          da Proforma
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#0A0F1A] font-medium">{item.client_name}</p>
                    <p className="text-xs text-[#4A5568]">
                      {formatDate(item.date)} | {(item.items || []).length} {t('invoice.items')} | {formatCurrency(item.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.status === 'bozza' && (
                      <Button variant="ghost" size="icon" onClick={() => handleSendSDI(item)} title={t('invoice.sendSDI')} data-testid={`invoice-sdi-${item.id}`}>
                        <PaperPlaneTilt size={18} className="text-green-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(item)} data-testid={`invoice-pdf-${item.id}`}>
                      <FilePdf size={18} className="text-red-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/fatture/${item.id}/edit`)} data-testid={`invoice-edit-${item.id}`}>
                      <PencilSimple size={18} className="text-[#4A5568]" />
                    </Button>
                    {!item.sdi_uuid && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`invoice-delete-${item.id}`}>
                        <Trash size={18} className="text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

